import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { ACTIVITY_SEEN_KEY } from "@/components/ActivityBar";
import { useRefetchOnActive } from "@/lib/useRefetch";
import { setActivityBadge } from "@/lib/badge";
import { timeAgo, countryFlag, fmtDuration } from "@/lib/format";
import { Eye, Send, Radar, HardHat, Clock, Users, CheckCircle2 } from "lucide-react";

const CONFIG = {
  view: { Icon: Eye, tint: "text-sky-400", bg: "bg-sky-500/15" },
  sent_back: { Icon: Send, tint: "text-amber-500", bg: "bg-amber-500/15" },
  verified: { Icon: CheckCircle2, tint: "text-emerald-500", bg: "bg-emerald-500/15" },
};

const META_TINT = { view: "text-sky-400", sent_back: "text-amber-500", verified: "text-emerald-500" };

function EventRow({ e, onOpen }) {
  const cfg = CONFIG[e.type] || CONFIG.view;
  const { Icon } = cfg;
  const title =
    e.type === "verified"
      ? "Verified — complete"
      : e.type === "sent_back"
        ? `Proof sent back${e.actor ? ` by ${e.actor}` : ""}`
        : `Link opened${e.country && e.country !== "—" ? ` ${countryFlag(e.country)}` : ""}`;

  const meta =
    e.type === "verified"
      ? (e.ttv_seconds != null ? `verified in ${fmtDuration(e.ttv_seconds)}` : "Owner verified")
      : e.type === "sent_back"
        ? (e.ttp_seconds != null ? `in ${fmtDuration(e.ttp_seconds)}` : "New proof in — tap to verify")
        : (e.opens
            ? `${e.opens} open${e.opens === 1 ? "" : "s"}${e.people ? ` · ${e.people} ${e.people === 1 ? "person" : "people"}` : ""}`
            : null);

  const MetaIcon = e.type === "view" ? Users : e.type === "verified" ? CheckCircle2 : Clock;

  return (
    <button
      data-testid={`activity-${e.type}-${e.report_id}`}
      onClick={() => onOpen(e)}
      className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition-colors active:bg-muted"
    >
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${cfg.bg}`}>
        <Icon className={`h-5 w-5 ${cfg.tint}`} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-semibold text-foreground">{title}</div>
        <div className="mt-0.5 flex items-center gap-1.5 truncate text-[13px] text-muted-foreground">
          <span className="truncate">{e.job_name || "Job"}</span>
          {e.trade && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              <HardHat className="h-3 w-3" />
              {e.trade}
            </span>
          )}
        </div>
        {meta && (
          <div
            data-testid={`activity-meta-${e.type}-${e.report_id}`}
            className={`mt-1 inline-flex items-center gap-1 text-[12px] font-semibold ${META_TINT[e.type] || "text-sky-400"}`}
          >
            <MetaIcon className="h-3 w-3" />
            {meta}
          </div>
        )}
      </div>
      <span className="shrink-0 text-xs text-muted-foreground/80">{timeAgo(e.at)}</span>
    </button>
  );
}

export default function ActivityFeed() {
  const navigate = useNavigate();
  const [events, setEvents] = useState(null);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/activity");
      const list = data.events || [];
      setEvents(list);
      // events are sorted newest-first — mark the latest as seen so the bar dot clears
      localStorage.setItem(ACTIVITY_SEEN_KEY, list.length ? list[0].at || new Date().toISOString() : new Date().toISOString());
      setActivityBadge(0);
    } catch {
      setEvents([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useRefetchOnActive(load);

  const openEvent = (e) => {
    if (e.job_id && e.report_id) navigate(`/jobs/${e.job_id}/reports/${e.report_id}`);
    else if (e.job_id) navigate(`/jobs/${e.job_id}`);
  };

  return (
    <AppShell title="Activity" onBack={() => navigate("/dashboard")}>
      <div className="mx-auto w-full max-w-md px-4 pb-6 pt-2 md:max-w-2xl">
        <h1 className="mb-1 font-heading text-3xl font-bold tracking-tight">Activity</h1>
        <p className="mb-5 text-sm text-muted-foreground">Every open and proof, as it happens.</p>

        {events === null ? (
          <div className="space-y-2.5" data-testid="activity-loading">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                <div className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 w-2/3 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="mt-16 flex flex-col items-center gap-3 text-center" data-testid="activity-empty">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <Radar className="h-8 w-8 text-muted-foreground" />
            </span>
            <p className="text-base font-semibold text-foreground">No activity yet</p>
            <p className="max-w-xs text-sm text-muted-foreground">
              Share a report link — you'll see the moment it's opened and when proof comes back.
            </p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="activity-list">
            {events.map((e, i) => (
              <EventRow key={`${e.type}-${e.report_id}-${e.at}-${i}`} e={e} onOpen={openEvent} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
